<template>
    <b-modal
        :visible="isOpen"
        @hidden="close"
        :lazy="true"
        title-tag="h2"
        title="Assign Lab"
    >
        <warning-message v-if="showWarning" />

        <b-row class="d-flex align-items-center form-item">
            <b-col cols="3">
                <b>Lab:</b>
            </b-col>
            <b-col>
                {{ labTestName }}
            </b-col>
        </b-row>

        <b-row class="d-flex align-items-center form-item">
            <b-col cols="3">
                <b>Patient:</b>
            </b-col>
            <b-col>
                <v-select
                    id="patient"
                    ref="vselect"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('patient') }"
                    :options="patientListOptions"
                    v-model="selectedPatient"
                    name="patient"
                    aria-placeholder="true"
                    aria-expanded="true"
                    placeholder="Select patient (required)"
                    @search="fetchPatients"
                ></v-select>
            </b-col>
        </b-row>

        <b-row class="d-flex align-items-center form-item">
            <b-col cols="3">
                <b>Record:</b>
            </b-col>
            <b-col>
                <b-form-select
                    v-model="labSource"
                    :disabled="selectedPatient === null"
                    :options="labSourceOptions"
                ></b-form-select>
            </b-col>
        </b-row>

        <template v-slot:modal-footer>
            <loader v-if="isLoading" class="m-0" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isLoading && labSource === 1"
                variant="primary"
                @click="next"
            >
                Next
            </b-button>
            <b-button
                v-else-if="!isLoading"
                variant="primary"
                @click="assign()"
                :disabled="labSource === 0"
            >
                Assign
            </b-button>
        </template>

        <modal-record
            ref="modalRecord"
            :hideBackdrop="true"
            @onSuccess="onSuccessModalRecord"
        />
    </b-modal>
</template>

<script>
import vSelect from "vue-select";
import Lab from "@/services/lab";
import AssignLabMixin from "@/mixins/assign-lab";
import modalRecord from "@/module/commons/record/create-update";

export default {
    components: {
        vSelect,
        modalRecord,
        Loader: () => import("@/components/elements/loader"),
        WarningMessage: () =>
            import("@/components/unassign-labs/assign-warning")
    },
    mixins: [AssignLabMixin],
    props: {
        reloadAfter: {
            type: Boolean
        }
    },
    data() {
        return {
            isOpen: false,
            isLoading: false,
            selectedPatient: null,
            patientListOptions: [],
            isDisableDropPatient: true,
            patientId: null,
            patientIndex: null,
            patientName: null,
            labResultId: null,
            labTestName: null,

            labSource: 0,
            labSourceOptions: [],
            record: null,
            labNameId: null,
            patientLabInfo: null
        };
    },
    computed: {
        businessId() {
            return this.$root.setting.business
                ? this.$root.setting.business.id
                : null;
        },
        showWarning() {
            return this.labSource !== 0 && this.labSource !== 1;
        }
    },
    watch: {
        selectedPatient(value) {
            if (value && this.patientLabInfo) {
                this.labSource = 0;
                this.patientId = value.id;
                this.labSourceOptions = this.getFilteredRecords(
                    this.patientLabInfo,
                    this.patientId
                );
            }
        }
    },
    methods: {
        open(record) {
            this.isOpen = true;
            this.record = record;

            this.patientName = record.patient.name || null;
            this.patientId = record.patient.id || null;
            this.patientIndex = Number(record.patient.index);
            this.labResultId = record.id;

            // auto-select patient (if any)
            if (this.patientName && this.patientIndex && this.patientId) {
                this.selectedPatient = {
                    label: `${this.patientName} (${this.patientIndex})`,
                    id: this.patientId,
                    index: this.patientIndex,
                    value: this.patientId
                };

                this.getOwner(this.selectedPatient);
            }

            this.getPatientLab(record);
        },
        close() {
            this.isOpen = false;
        },
        async getPatientLab(record) {
            const response = await Lab.getPatientLab(record.id, record.module);

            let info = response.resources;
            this.patientLabInfo = info;

            if (info.item && info.item.display_name) {
                this.labTestName = info.item.display_name;
            } else {
                let name = info.product_info_rotor_name
                    ? info.product_info_rotor_name
                    : info.analyzer_type;
                this.labTestName = info.source + ": " + name;
            }

            this.labSourceOptions = this.getFilteredRecords(
                info,
                this.patientId
            );
        },
        getFilteredRecords(patientLabInfo, patientId) {
            let labSourceOptions = this.getDefaultOptions();
            const { options, patient_record } = this.patientLabInfo;

            if (options) {
                let filteredRecordIds = [];
                let result = [];

                for (const recordId in patient_record) {
                    if (patient_record[recordId] === patientId) {
                        filteredRecordIds.push(recordId);
                    }
                }

                for (const key of filteredRecordIds) {
                    result.push({
                        value: key,
                        text: options[key]
                    });
                }

                labSourceOptions = [...labSourceOptions, ...result];
            }

            return labSourceOptions;
        },
        next() {
            this.isLoading = true;
            let params = {
                id: this.selectedPatient.id,
                sample_id: this.record.id
            };

            let clientID = this.selectedPatient.owner
                ? this.selectedPatient.owner.id
                : null;

            Lab.getAddDialogDetails(params)
                .then(response => {
                    this.isLoading = false;

                    this.$refs.modalRecord.open({
                        action: "assign_lab",
                        module: this.MODULE.PATIENT,
                        clientID,
                        patient: {
                            id: this.selectedPatient.id
                        },
                        is_lab: true,
                        lab_data: response.form_data,
                        hideSendToLab: true
                    });
                })
                .catch(error => {
                    this.isLoading = false;
                    this._message(
                        "Unable to get details to proceed to new record",
                        "danger"
                    );
                    console.error(error);
                });
        },
        assign() {
            this.isLoading = true;
            let formData = new FormData();

            formData.append("patient_id", this.selectedPatient.id);
            formData.append(
                "patient",
                this.selectedPatient.fullname || this.selectedPatient.label
            );
            formData.append("sample_id", this.record.id);
            formData.append("record", this.labSource);

            Lab.savePatientLab(formData)
                .then(() => {
                    this._message("Successfully assigned lab");
                    this.$emit("reload");
                    this.isLoading = false;
                    this.isOpen = false;
                })
                .catch(error => {
                    this._message("Unable to assign lab", "danger");
                    console.error(error);
                });
        },
        fetchPatients(search) {
            this.searchPatients(search, this);
        },
        onSuccessModalRecord() {
            this._message("Results successfully assigned");
            this.$emit("reload");
            this.close();
        }
    }
};
</script>

<style scoped>
.preloader {
    margin: 8px 0;
}
.form-item {
    margin-bottom: 10px;
}
span.no-result {
    margin-left: 10px;
}
</style>
